import { useEffect, useState } from "react";
import TextBoxT1 from "../controls/TextBoxT1";
import TextBoxDateTime from "../controls/TextBoxDateTime";
import { FaInfoCircle } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import Alert from "@mui/material/Alert";
import DropDownSearch from "../controls/DropDownSearch";
import DropDownSearchD2 from "../controls/DropDownSearchD2";
import ProductCatalogueMultiSelect from "../controls/ProductCatalogueMultiSelect";
import AddressLookup from "./AddressLookup";

const Step1 = ({
  setText,
  text,
  ddKommun,
  ddProdukt,
  agreementInfoText,
  agreementInfoSeverity,
  remValKommun,
  setRemValKommun,
  remValLine,
  setRemValLine,
  sla,
  setSla,
  account,
  validateIsErr,
  isBack = false,
  setIsBack,
  productCatalogueItems,
  hasExtraProducts,
}) => {
  const [productInfoText, setProductInfoText] = useState(null);

  const isEmptyOrWhitespace = (str) => {
    return !str || str.trim() === "";
  };

  const selectedCatalogueItems = Array.isArray(text?.productCatalogueItems)
    ? text.productCatalogueItems
    : [];

  const shouldHideProductDropdown = hasExtraProducts;

  const applySelectedProduct = (obj) => {
    if (!obj) return;

    setRemValLine(JSON.stringify(obj));
    setProductInfoText(obj.ser_contr_line_alt_desc_20);

    setText((prev) => ({
      ...prev,
      lineNo: obj.line_no,
      contract_id: obj.contract_id,
      maint_org: obj.maint_org,
      work_type_id: obj.work_type_id,
      product: isEmptyOrWhitespace(obj.ser_contr_line_alt_desc)
        ? obj.description
        : obj.ser_contr_line_alt_desc,
      //shortDesc: `${prev.kommun} - ${prev.referensnummer}`,
    }));

    setSla((prev) => ({
      ...prev,
      min_end_time: obj.min_end_time,
      min_start_time: obj.min_start_time,
    }));

    setIsBack(false);
  };

  const clearSelectedProduct = () => {
    setRemValLine("");
    setProductInfoText(null);

    setText((prev) => ({
      ...prev,
      lineNo: null,
      contract_id: null,
      maint_org: null,
      work_type_id: null,
      product: "",
      //shortDesc: prev.kommun ? `${prev.kommun} - ` : "",
    }));

    setSla((prev) => ({
      ...prev,
      min_end_time: null,
      min_start_time: null,
    }));

    setIsBack(false);
  };

  // Auto select only when the normal product dropdown is used
  useEffect(() => {
    if (hasExtraProducts) {
      return;
    }

    const productCount = Array.isArray(ddProdukt) ? ddProdukt.length : 0;
    const shouldAutoSelectSingleProduct = productCount === 1;

    if (!shouldAutoSelectSingleProduct) {
      return;
    }

    const onlyProduct = ddProdukt[0];
    const serializedProduct = JSON.stringify(onlyProduct);

    if (remValLine !== serializedProduct) {
      applySelectedProduct(onlyProduct);
    }
  }, [hasExtraProducts, ddProdukt, remValLine]);

  // Auto select product based on highest selected SlaClass
  useEffect(() => {
    if (!hasExtraProducts) {
      return;
    }

    if (!Array.isArray(ddProdukt) || ddProdukt.length === 0) {
      clearSelectedProduct();
      return;
    }

    if (
      !Array.isArray(selectedCatalogueItems) ||
      selectedCatalogueItems.length === 0
    ) {
      clearSelectedProduct();
      return;
    }

    const slaValues = selectedCatalogueItems
      .map((item) => item?.slaClass)
      .filter((value) => value !== null && value !== undefined);

    if (slaValues.length === 0) {
      clearSelectedProduct();
      return;
    }

    const lowestSlaClass = Math.min(...slaValues);

    const matchingProduct = ddProdukt.find((item) => {
      const numericDescription = Number(item?.description);
      return (
        !Number.isNaN(numericDescription) &&
        numericDescription === lowestSlaClass
      );
    });

    if (!matchingProduct) {
      clearSelectedProduct();
      return;
    }

    const serializedProduct = JSON.stringify(matchingProduct);

    if (remValLine !== serializedProduct) {
      applySelectedProduct(matchingProduct);
    }
  }, [hasExtraProducts, selectedCatalogueItems, ddProdukt, remValLine]);

  useEffect(() => {
    setProductInfoText(null);
  }, [remValKommun]);

  const showAgreementInfo = agreementInfoText && agreementInfoText.trim();

  return (
    <>
      <div className="main-container">
        <div className="div-table">
          <div className="div-table-row">
            <div className="div-table-col-header">
              <h3 className="stepH3">
                Grundläggande information{" "}
                <FaInfoCircle
                  id="ttStep1"
                  className="infoCircelTop"
                  data-tooltip-html="Alla fälten här är obligatoriska."
                />
                <Tooltip
                  anchorId="ttStep1"
                  className="infoCircelTop2"
                  multiline={true}
                  data-html={true}
                  html
                />
              </h3>
            </div>
          </div>
          <div className="div-table-row">
            <div
              className="div-table-col"
              style={{ width: "500px", marginBottom: "20px" }}
            >
              <AddressLookup
                ddKommun={ddKommun}
                setText={setText}
                text={text}
                setRemValKommun={setRemValKommun}
              />
            </div>
          </div>
          <div className="div-table-row">
            <div className="div-table-col">
              <DropDownSearch
                headerText="Kommun"
                ddData={ddKommun}
                setText={setText}
                text={text}
                jsonText="kobject"
                tooltipText="Här väljer du den kommun där jobbet ska utföras."
                remValKommun={remValKommun}
                setRemValKommun={setRemValKommun}
                sla={sla}
                setSla={setSla}
                errText="Måste ha ett värde."
                validateIsErr={validateIsErr}
              />
            </div>
          </div>

          <div className="div-table-col">
            <TextBoxT1
              headerText="Referensnummer"
              jsonText="referensnummer"
              setText={setText}
              text={text}
              toolTip="Skriv in erat id för denna arbetsorder. Godkända tecken är bokstäver, siffror, komma och bindestreck."
              validateIsErr={validateIsErr}
              errText="Obligatorisk, endast bokstäver, siffror, bindestreck och komma."
              minLength="3"
              maxLength="20"
            />
          </div>

          {!shouldHideProductDropdown && (
            <div className="div-table-row">
              <div className="div-table-col" style={{ width: "500px" }}>
                <DropDownSearchD2
                  headerText="Produkt"
                  ddData={ddProdukt}
                  setText={setText}
                  text={text}
                  jsonText="product"
                  tooltipText="Här väljer du den produkt du vill beställa."
                  remValLine={remValLine}
                  setRemValLine={setRemValLine}
                  sla={sla}
                  setSla={setSla}
                  errText="Måste ha ett värde."
                  validateIsErr={validateIsErr}
                  setIsBack={setIsBack}
                  setProductInfoText={setProductInfoText}
                />
              </div>
            </div>
          )}

          {hasExtraProducts && (
            <div className="div-table-row">
              <div className="div-table-col" style={{ width: "500px" }}>
                <ProductCatalogueMultiSelect
                  headerText="Tillägg / Produktkatalog"
                  tooltipText=""
                  items={productCatalogueItems || []}
                  text={text}
                  setText={setText}
                  jsonText="productCatalogueItems"
                />
              </div>
            </div>
          )}

          <div className="div-table-row">
            <div className="div-table-col">
              <TextBoxT1
                headerText="Kort beskrivning"
                jsonText="shortDesc"
                setText={setText}
                text={text}
                toolTip="Kort beskrivning för arbetsordern. Använd föreslagen eller skriv en egen."
                validateIsErr={validateIsErr}
                errText="Obligatorisk"
                minLength="5"
                maxLength="55"
                clsname="txtBox2_big"
              />
            </div>
          </div>

          <div className="div-table-row">
            <div className="div-table-col">
              <TextBoxDateTime
                headerText="Starttid"
                jsonText="requiredStartDate"
                setText={setText}
                text={text}
                sla={sla}
                errText="Fel datumformat eller intervall."
                validateIsErr={validateIsErr}
                toolTip="Här väljer du önskad tid för när Eltel ska påbörja jobbet i fält.<br />Obs! Vid avtalad SLA ska jobbet påbörjas vid denna tid."
                isBack={isBack}
              />
            </div>
            <div className="div-table-col">
              <TextBoxDateTime
                headerText="Sluttid"
                jsonText="requiredEndDate"
                setText={setText}
                text={text}
                sla={sla}
                errText="Fel datumformat eller intervall."
                validateIsErr={validateIsErr}
                toolTip="Här väljer du önskad klar tid för när Eltel ska slutföra jobbet i fält.<br />Obs vid avtalad SLA ska jobbet slutföras enligt avtalets direktiv."
                isBack={isBack}
              />
            </div>
          </div>

          <div className="div-table-row">
            <div className="div-table-col">
              <TextBoxT1
                headerText="Kontaktperson beställare"
                jsonText="clientContact"
                setText={setText}
                text={text}
                validateIsErr={validateIsErr}
                maxLength="30"
              />
            </div>
          </div>

          <div className="div-table-row">
            <div className="div-table-col">
              <TextBoxT1
                headerText="E-mail beställare"
                jsonText="clientContactEmail"
                placeholder="namn@foretag.se"
                setText={setText}
                text={text}
                validateIsErr={validateIsErr}
                maxLength="112"
                errText="Format ej korekt."
              />
            </div>
            <div className="div-table-col">
              <TextBoxT1
                headerText="Telefon beställare"
                jsonText="clientPhone"
                placeholder=""
                setText={setText}
                text={text}
                validateIsErr={validateIsErr}
                maxLength="12"
                errText="Obligatorisk,+ och 0-9 är ok"
              />
            </div>
          </div>
        </div>

        {!showAgreementInfo && productInfoText && productInfoText.trim() && (
          <div className="description-container">
            <p id="description-text">{productInfoText}</p>
          </div>
        )}

        <div className="div-table-col step1-alert-col">
          {showAgreementInfo && (
            <Alert severity={agreementInfoSeverity || "warning"}>
              {agreementInfoText}
            </Alert>
          )}
        </div>
      </div>
    </>
  );
};

export default Step1;
